/**
 * eSIM Product Page JavaScript
 * Handles variant selection, tab switching, and description parsing
 */

class EsimProductPage extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.variants = this.querySelectorAll('.esim-product__variant');
    this.tabs = this.querySelectorAll('.esim-product__tab');
    this.checkoutBtn = this.querySelector('.esim-product__checkout-btn');
    this.checkoutText = this.querySelector('.esim-product__checkout-text');
  }

  connectedCallback() {
    this.initVariantSelection();
    this.initTabs();
    this.parseProductDescription();
  }

  /**
   * Initialize variant selection handlers
   */
  initVariantSelection() {
    this.variants.forEach(variant => {
      variant.addEventListener('click', (e) => {
        if (variant.classList.contains('esim-product__variant--unavailable')) {
          return;
        }

        // Update selection state
        this.variants.forEach(v => v.classList.remove('esim-product__variant--selected'));
        variant.classList.add('esim-product__variant--selected');

        // Update radio button
        const radio = variant.querySelector('.esim-product__variant-radio');
        if (radio) {
          radio.checked = true;
        }

        // Update hidden variant ID input
        const variantId = variant.dataset.variantId;
        if (this.variantInput) {
          this.variantInput.value = variantId;
        }

        // Update checkout button state
        this.updateCheckoutButton(variant.dataset.variantAvailable === 'true');

        // Update URL without page reload
        this.updateURL(variantId);

        // Dispatch custom event for any listeners
        this.dispatchEvent(new CustomEvent('variant:changed', {
          detail: {
            variantId: variantId,
            price: variant.dataset.variantPrice,
            available: variant.dataset.variantAvailable === 'true'
          },
          bubbles: true
        }));
      });
    });
  }

  /**
   * Update the checkout button state
   */
  updateCheckoutButton(available) {
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = !available;
    }
    if (this.checkoutText) {
      this.checkoutText.textContent = available ? 'Go to secure checkout' : 'Sold out';
    }
  }

  /**
   * Update URL with selected variant
   */
  updateURL(variantId) {
    if (!variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Initialize tab switching
   */
  initTabs() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        // Update active state
        this.tabs.forEach(t => t.classList.remove('esim-product__tab--active'));
        tab.classList.add('esim-product__tab--active');

        // Could show/hide different content based on tab
        // For now, just visual switching
        const tabType = tab.dataset.tab;
        this.dispatchEvent(new CustomEvent('tab:changed', {
          detail: { tab: tabType },
          bubbles: true
        }));
      });
    });
  }

  /**
   * Parse product description HTML to extract structured data
   */
  parseProductDescription() {
    const descriptionContainer = document.getElementById(`esim-description-data-${this.sectionId}`);
    if (!descriptionContainer) return;

    const html = descriptionContainer.innerHTML;

    // Parse networks from Technical Specs
    this.parseNetworks(html);

    // Parse technical details
    this.parseTechnicalDetails(html);

    // Parse activation info
    this.parseActivation(html);
  }

  /**
   * Parse network information from description
   */
  parseNetworks(html) {
    const networksContainer = document.getElementById(`esim-networks-${this.sectionId}`);
    if (!networksContainer) return;

    // Look for Networks data in the HTML
    const networksMatch = html.match(/data-spec="networks"[^>]*>[\s\S]*?<strong>Networks:<\/strong>\s*([\s\S]*?)<\/li>/i);

    if (networksMatch && networksMatch[1]) {
      const networksText = networksMatch[1].trim();
      // Parse format like "ZM - Airtel 5G, MTN 4G" or "TH - AIS 4G/5G, True 4G/5G"
      const networkParts = networksText.split(',').map(n => n.trim());

      let networksHtml = '';
      networkParts.forEach(network => {
        // Extract network name and speed
        const parts = network.split(/\s+/);
        let name = '';
        let badge = '';

        // Find the network name and badge
        parts.forEach(part => {
          if (part.match(/\d+G/i) || part.match(/\d+G\/\d+G/i)) {
            badge = part.toUpperCase();
          } else if (part !== '-' && part.length > 1) {
            name += (name ? ' ' : '') + part;
          }
        });

        if (name) {
          networksHtml += `
            <div class="esim-product__network">
              <span class="esim-product__network-name">${name}</span>
              ${badge ? `<span class="esim-product__network-badge">${badge}</span>` : ''}
            </div>
          `;
        }
      });

      if (networksHtml) {
        networksContainer.innerHTML = networksHtml;
      }
    }
  }

  /**
   * Parse technical details (hotspot, speed)
   */
  parseTechnicalDetails(html) {
    // Parse Hotspot
    const hotspotContainer = document.getElementById(`esim-hotspot-${this.sectionId}`);
    if (hotspotContainer) {
      const hotspotMatch = html.match(/data-spec="hotspot"[^>]*>[\s\S]*?<strong>Hotspot:<\/strong>\s*([\s\S]*?)<\/li>/i);
      if (hotspotMatch && hotspotMatch[1]) {
        const value = hotspotMatch[1].trim().replace(/<[^>]+>/g, '');
        hotspotContainer.textContent = value === 'Yes' ? 'Unlimited' : value;
      }
    }

    // Parse Speed Reduction
    const speedContainer = document.getElementById(`esim-speed-${this.sectionId}`);
    if (speedContainer) {
      const speedMatch = html.match(/data-spec="speed-reduction"[^>]*>[\s\S]*?<strong>Speed Reduction:<\/strong>\s*([\s\S]*?)<\/li>/i);
      if (speedMatch && speedMatch[1]) {
        const value = speedMatch[1].trim().replace(/<[^>]+>/g, '');
        speedContainer.textContent = value === 'No' ? 'Never' : value;
      }
    }

    // Parse validity days from Pre-Activation
    const validityContainer = document.getElementById(`esim-validity-${this.sectionId}`);
    if (validityContainer) {
      const validityMatch = html.match(/data-spec="pre-activation"[^>]*>[\s\S]*?<strong>Pre-Activation Days:<\/strong>\s*(\d+)/i);
      if (validityMatch && validityMatch[1]) {
        validityContainer.textContent = validityMatch[1];
      }
    }
  }

  /**
   * Parse activation information
   */
  parseActivation(html) {
    const activationContainer = document.getElementById(`esim-activation-${this.sectionId}`);
    if (!activationContainer) return;

    // Look for description text
    const descMatch = html.match(/box-description[\s\S]*?<p>([\s\S]*?)<\/p>/i);
    if (descMatch && descMatch[1]) {
      const descText = descMatch[1].replace(/<[^>]+>/g, '').trim();
      if (descText.length > 20) {
        // Update to more specific activation text
        const countryMatch = html.match(/Supported Countries:<\/strong>\s*([\s\S]*?)<\/li>/i);
        if (countryMatch) {
          const country = countryMatch[1].replace(/<[^>]+>/g, '').trim();
          activationContainer.textContent = `Activates instantly, connects automatically when arriving ${country}`;
        }
      }
    }
  }
}

// Register custom element
if (!customElements.get('esim-product-page')) {
  customElements.define('esim-product-page', EsimProductPage);
}

// Handle form submission for AJAX add to cart
document.addEventListener('DOMContentLoaded', () => {
  const esimForms = document.querySelectorAll('.esim-product form[data-type="add-to-cart-form"]');

  esimForms.forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector('.esim-product__checkout-btn');
      const originalText = submitBtn.querySelector('.esim-product__checkout-text').textContent;

      // Show loading state
      submitBtn.disabled = true;
      submitBtn.querySelector('.esim-product__checkout-text').textContent = 'Adding...';

      try {
        const formData = new FormData(form);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          // Redirect to cart or show success
          window.location.href = '/cart';
        } else {
          const error = await response.json();
          console.error('Add to cart error:', error);
          submitBtn.querySelector('.esim-product__checkout-text').textContent = originalText;
          submitBtn.disabled = false;
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        submitBtn.querySelector('.esim-product__checkout-text').textContent = originalText;
        submitBtn.disabled = false;
      }
    });
  });
});
